import { Helmet } from "react-helmet-async";
import { SITE } from "@/config/site";
import { FEATURED } from "@/data/featured";
import { mediaUrl } from "@/lib/media";

interface SeoProps {
  title: string;
  description: string;
  /** Path beginning with "/" — used to build the canonical URL. */
  path: string;
  /** Absolute or site-relative image URL for social cards. */
  image?: string;
  /** Optional JSON-LD structured data object(s). */
  jsonLd?: object | object[];
  type?: "website" | "article";
  noindex?: boolean;
  /** Image URL to preload as the LCP candidate (e.g. a hero poster). */
  preloadImage?: string;
}

export function Seo({
  title,
  description,
  path,
  image,
  jsonLd,
  type = "website",
  noindex = false,
  preloadImage,
}: SeoProps) {
  const canonical = `${SITE.url}${path === "/" ? "" : path}`;
  const fullTitle = path === "/" ? `${SITE.name} — ${SITE.tagline}` : `${title} • ${SITE.name}`;
  const ogImage = image ?? mediaUrl(FEATURED.ceremony);
  const blocks = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      {preloadImage && <link rel="preload" as="image" href={preloadImage} />}
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE.name} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:locale" content="en_US" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {blocks.map((block, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(block)}
        </script>
      ))}
    </Helmet>
  );
}
